import * as amplitude from "@amplitude/analytics-browser";
import { useRequiredContext } from "@redotech/react-util/context";
import { useHandler } from "@redotech/react-util/hook";
import { T, useTranslation } from "@redotech/redo-i18n";
import AlertCircleIcon from "@redotech/redo-icons/alert-circle.svg";
import CloseIcon from "@redotech/redo-icons/x-close.svg";
import { MerchantRoutesContext } from "@redotech/redo-merchant-app-common/merchant-routes";
import {
  isReturnsSettingsTab,
  LEGACY_SETTINGS_PATH_BY_TAB,
  legacySettingsPathForTab,
  RETURNS_SETTINGS_TAB_QUERY_PARAM,
  ReturnsSettingsTab,
} from "@redotech/redo-merchant-app-common/settings/returns-settings-tab";
import { useReturnsSettingsExperience } from "@redotech/redo-merchant-app-common/settings/use-returns-settings-experience";
import { RETURNS_CONSOLIDATED_SETTINGS_PATH } from "@redotech/redo-settings-common/flat-team-settings/returns/settings-paths";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { RedoFeaturedIcon } from "@redotech/redo-web/arbiter-components/featured-icon/redo-featured-icon";
import { Flex } from "@redotech/redo-web/flex";
import { Text } from "@redotech/redo-web/text";
import { memo, useState } from "react";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useLocalStorage } from "usehooks-ts";
import { PlatformEvents } from "../../../platform-events";
import { formatOldSettingsShutoffDate } from "../../../setting/returns/switch-back/old-settings-shutoff";
import { SwitchBackModal } from "../../../setting/returns/switch-back/switch-back-modal";
import * as css from "./returns-settings-switch-back-banner.module.css";

const DISMISSED_STORAGE_KEY =
  "redo.returns-settings.switch-back-banner-dismissed";

export const ReturnsSettingsSwitchBackBanner = memo(
  function ReturnsSettingsSwitchBackBanner() {
    const { t } = useTranslation();
    const routes = useRequiredContext(MerchantRoutesContext);
    const navigate = useNavigate();
    const { pathname } = useLocation();
    const [searchParams] = useSearchParams();
    const experience = useReturnsSettingsExperience();
    const [dismissed, setDismissed] = useLocalStorage(
      DISMISSED_STORAGE_KEY,
      false,
    );
    const [modalOpen, setModalOpen] = useState(false);
    const openModal = useHandler(() => setModalOpen(true));

    const switchBack = useHandler(() => {
      const tab = searchParams.get(RETURNS_SETTINGS_TAB_QUERY_PARAM);
      experience.switchToLegacy();
      setModalOpen(false);
      const legacyPath =
        (isReturnsSettingsTab(tab) && legacySettingsPathForTab(tab)) ||
        LEGACY_SETTINGS_PATH_BY_TAB[ReturnsSettingsTab.COVERAGE];
      navigate(routes.getSettingsSubRoute(legacyPath));
    });

    const dismiss = useHandler(() => {
      setDismissed(true);
      amplitude.track(
        PlatformEvents.RETURNS_SETTINGS_SWITCH_BACK_BANNER_DISMISSED,
      );
    });

    const onReturnsSettings =
      pathname ===
      routes.getSettingsSubRoute(RETURNS_CONSOLIDATED_SETTINGS_PATH);
    if (
      dismissed ||
      !onReturnsSettings ||
      !experience.useNewUi ||
      !experience.canSwitch
    ) {
      return null;
    }

    return (
      <>
        <Flex
          align="center"
          bgColor="primary"
          borderBottomWidth="1px"
          borderColor="primary"
          borderStyle="solid"
          gap="lg"
          px="3xl"
          py="lg"
          w="full"
        >
          <RedoFeaturedIcon color="gray" Icon={AlertCircleIcon} />
          <Flex grow={1}>
            <Text fontSize="md" textColor="secondary">
              <T
                components={{
                  lead: (
                    <Text
                      as="span"
                      fontSize="md"
                      fontWeight="semibold"
                      textColor="primary"
                    >
                      {null}
                    </Text>
                  ),
                  bold: (
                    <Text
                      as="span"
                      fontSize="md"
                      fontWeight="bold"
                      textColor="primary"
                    >
                      {null}
                    </Text>
                  ),
                  cta: (
                    <RedoButton
                      buttonClassName={css.inlineLinkButton}
                      className={css.inlineLink}
                      dataTrackId="merchant.settings.returns.switch-back.banner-link"
                      hierarchy="link_underline"
                      onClick={openModal}
                      size="xl"
                      text={t("switch back", {
                        context: "link to return to the old returns settings",
                      })}
                      textWeight="regular"
                    />
                  ),
                }}
                i18nKey="<lead>We've updated returns settings.</lead> You can <cta/> to the previous experience for now. This update becomes permanent on <bold>{{date}}</bold>."
                values={{ date: formatOldSettingsShutoffDate() }}
              />
            </Text>
          </Flex>
          <RedoButton
            aria-label={t("Dismiss", { context: "notification banner" })}
            IconLeading={CloseIcon}
            onClick={dismiss}
            size="sm"
          />
        </Flex>
        <SwitchBackModal
          onCancel={() => setModalOpen(false)}
          onConfirm={switchBack}
          open={modalOpen}
        />
      </>
    );
  },
);
